import { SelectedOptionData } from '../../data/model';
import { ShoppingCartSummaryTranslation } from '../../localization/SiteTranslation';
import ComponentList from '../../shared/components/ComponentList/ComponentList';
import { ExpandablePanel } from '../../shared/components/ExpandablePanel/ExpandablePanel';
import Icon from '../../shared/components/Icon/Icon';
import List from '../../shared/components/List/List';
import { LoadableImage } from '../../shared/components/LoadableImage';
import { Subtitle1DarkGrey } from '../../shared/components/Typography/Subtitle';
import { IConfigurationComponentState } from './IConfigurationComponentState';
import css from './ModularConfigurationSummary.css';

interface ModularConfigurationSummaryProps {
  components: IConfigurationComponentState[];
  configurationCode: string;
  productFamilyName: string;
  translation: ShoppingCartSummaryTranslation;
  className?: string;
}

export default function ModularConfigurationSummary({
  className,
  configurationCode,
  components,
  productFamilyName,
  translation,
}: ModularConfigurationSummaryProps) {
  return (
    <div className={className}>
      <h2 className={css.title}>{productFamilyName}</h2>
      {configurationCode && <Subtitle1DarkGrey>{configurationCode}</Subtitle1DarkGrey>}
      <List
        itemClassName={css.component}
        items={components}
        itemRenderer={(item: IConfigurationComponentState) => (
          <ConfigurationComponent component={item} translation={translation} />
        )}
      />
    </div>
  );
}

interface ConfigurationComponentProps {
  component: IConfigurationComponentState;
  translation: ShoppingCartSummaryTranslation;
}

function ConfigurationComponent({ component, translation }: ConfigurationComponentProps) {
  return (
    <>
      {component.imageUrl && (
        <div className={css.componentImageWrapper}>
          <LoadableImage
            className={css.image}
            src={component.imageUrl}
            width={100}
            height={150}
            sharpeningFactor={1.5}
          />
        </div>
      )}
      <div className={css.componentDetails}>
        <h3 className={css.componentName}>
          {component.displayName} ({component.counter})
        </h3>
        <Subtitle1DarkGrey>{component.configurationCode}</Subtitle1DarkGrey>
        <OptionsAndAccessories
          className={css.features}
          accessories={component.accessories}
          options={component.options}
          translation={translation}
        />
      </div>
    </>
  );
}

interface OptionsAndAccessoriesProps {
  accessories: SelectedOptionData[];
  options: SelectedOptionData[];
  translation: ShoppingCartSummaryTranslation;
  className?: string;
}

function OptionsAndAccessories({ accessories, className, options, translation }: OptionsAndAccessoriesProps) {
  const renderPanelButton = (isExpanded: boolean) => (
    <button className={css.button}>
      {isExpanded ? (
        <>
          {translation.lessInformationText} <Icon name="minus" className={css.icon} />
        </>
      ) : (
        <>
          {translation.moreInformationText} <Icon name="plus" className={css.icon} />
        </>
      )}
    </button>
  );

  if (accessories.empty() && options.empty()) {
    return null;
  }

  return (
    <ExpandablePanel className={className} renderTitle={renderPanelButton} expanded={false}>
      <ComponentList accessories={accessories} options={options} />
    </ExpandablePanel>
  );
}
